import jsPDF from 'jspdf';
import { WAIVER_TEXT, BUSINESS } from './waiverText';

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function safeName(name) {
  return (name || 'waiver').replace(/[^a-z0-9]+/gi, '_').slice(0, 60);
}

export function exportCSV(waivers) {
  const header = ['ID', 'Name', 'Phone', 'Email', 'Date', 'Signed At'];
  const rows = waivers.map((w) => [
    w.id,
    w.name,
    w.phone,
    w.email,
    w.date,
    new Date(w.signedAt).toISOString(),
  ]);
  const escape = (v) => {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const csv = [header, ...rows].map((r) => r.map(escape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `kryoteks_waivers_${new Date().toISOString().slice(0, 10)}.csv`);
}

export function exportJSON(waivers) {
  const blob = new Blob([JSON.stringify(waivers, null, 2)], {
    type: 'application/json',
  });
  downloadBlob(
    blob,
    `kryoteks_waivers_${new Date().toISOString().slice(0, 10)}.json`
  );
}

export function exportSinglePDF(waiver) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usable = pageWidth - margin * 2;

  // Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(0, 108, 220);
  doc.text(BUSINESS.name, margin, 64);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text(BUSINESS.address, margin, 82);
  doc.text(`Phone: ${BUSINESS.phone}  ·  Email: ${BUSINESS.email}  ·  By Appointment Only`, margin, 96);

  doc.setDrawColor(0, 168, 255);
  doc.setLineWidth(1.5);
  doc.line(margin, 108, pageWidth - margin, 108);

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(15, 23, 42);
  doc.text('Liability Waiver & Release of Claims', margin, 132);

  // Client info block
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const infoY = 158;
  const infoLines = [
    `Name:     ${waiver.name}`,
    `Phone:    ${waiver.phone}`,
    `Email:    ${waiver.email}`,
    `Date:     ${waiver.date}`,
    `Signed:   ${new Date(waiver.signedAt).toLocaleString()}`,
    `ID:       ${waiver.id}`,
  ];
  infoLines.forEach((l, i) => doc.text(l, margin, infoY + i * 14));

  // Waiver text
  doc.setFontSize(9);
  doc.setTextColor(30, 30, 30);
  const bodyY = infoY + infoLines.length * 14 + 12;
  const lines = doc.splitTextToSize(WAIVER_TEXT, usable);
  let cursorY = bodyY;
  lines.forEach((line) => {
    if (cursorY > pageHeight - 180) {
      doc.addPage();
      cursorY = margin;
    }
    doc.text(line, margin, cursorY);
    cursorY += 12;
  });

  // Signature section
  if (cursorY > pageHeight - 180) {
    doc.addPage();
    cursorY = margin;
  }
  cursorY += 16;
  doc.setDrawColor(77, 208, 225);
  doc.setLineWidth(0.8);
  doc.line(margin, cursorY, pageWidth - margin, cursorY);
  cursorY += 20;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(0, 108, 220);
  doc.text('Client Signature', margin, cursorY);
  cursorY += 10;

  if (waiver.signature && waiver.signature.startsWith('data:image')) {
    try {
      doc.addImage(waiver.signature, 'PNG', margin, cursorY, 260, 100);
    } catch (e) {
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(120, 120, 120);
      doc.text('[Signature could not be embedded]', margin, cursorY + 20);
    }
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(
    `Printed name: ${waiver.name}`,
    margin,
    cursorY + 118
  );
  doc.text(
    `Date signed: ${new Date(waiver.signedAt).toLocaleDateString()}`,
    margin,
    cursorY + 132
  );

  doc.save(`kryoteks_waiver_${safeName(waiver.name)}_${waiver.date}.pdf`);
}

export function exportAllPDF(waivers) {
  if (!waivers.length) return;
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usable = pageWidth - margin * 2;

  waivers.forEach((waiver, idx) => {
    if (idx > 0) doc.addPage();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(0, 108, 220);
    doc.text(BUSINESS.name, margin, 64);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(BUSINESS.address, margin, 82);
    doc.text(`Phone: ${BUSINESS.phone}  ·  Email: ${BUSINESS.email}  ·  By Appointment Only`, margin, 96);

    doc.setDrawColor(0, 168, 255);
    doc.setLineWidth(1.5);
    doc.line(margin, 108, pageWidth - margin, 108);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(15, 23, 42);
    doc.text('Liability Waiver & Release of Claims', margin, 132);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const infoY = 158;
    const infoLines = [
      `Name:     ${waiver.name}`,
      `Phone:    ${waiver.phone}`,
      `Email:    ${waiver.email}`,
      `Date:     ${waiver.date}`,
      `Signed:   ${new Date(waiver.signedAt).toLocaleString()}`,
      `ID:       ${waiver.id}`,
    ];
    infoLines.forEach((l, i) => doc.text(l, margin, infoY + i * 14));

    doc.setFontSize(9);
    doc.setTextColor(30, 30, 30);
    const bodyY = infoY + infoLines.length * 14 + 12;
    const lines = doc.splitTextToSize(WAIVER_TEXT, usable);
    let cursorY = bodyY;
    lines.forEach((line) => {
      if (cursorY > pageHeight - 180) {
        doc.addPage();
        cursorY = margin;
      }
      doc.text(line, margin, cursorY);
      cursorY += 12;
    });

    if (cursorY > pageHeight - 180) {
      doc.addPage();
      cursorY = margin;
    }
    cursorY += 16;
    doc.setDrawColor(0, 168, 255);
    doc.line(margin, cursorY, pageWidth - margin, cursorY);
    cursorY += 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 108, 220);
    doc.text('Client Signature', margin, cursorY);
    cursorY += 10;

    if (waiver.signature && waiver.signature.startsWith('data:image')) {
      try {
        doc.addImage(waiver.signature, 'PNG', margin, cursorY, 260, 100);
      } catch (e) {
        // continue
      }
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text(`Printed name: ${waiver.name}`, margin, cursorY + 118);
    doc.text(
      `Date signed: ${new Date(waiver.signedAt).toLocaleDateString()}`,
      margin,
      cursorY + 132
    );
  });

  doc.save(`kryoteks_waivers_all_${new Date().toISOString().slice(0, 10)}.pdf`);
}
